import { ManualInputFlowType } from 'app/features/manual-input-registration/models';

import { ResultDeterminationType } from '../../../models/result-determination-type';
import { LocationType } from '../../location-selector/models';
import { Activity, ActivityType } from '../models';

// Not all break activities have generic location, some will be null
export const isSupportedBreakActivity = (a: Activity) =>
    (a.locationType === LocationType.Generic && a.activityType === ActivityType.Break) ||
    (a.locationType === null && a.activityType === ActivityType.Break);

const supportedLocationTypes: LocationType[] = [
    LocationType.Generic,
    LocationType.Greenhouse,
    LocationType.Packhouse,
    LocationType.Package,
    LocationType.Crop,
    LocationType.Door,
    LocationType.Site,
    LocationType.Row,
];

const supportedResultDeterminationTypes: ResultDeterminationType[] = [
    ResultDeterminationType.byAmount,
    ResultDeterminationType.byAmountAndProductBox,
    ResultDeterminationType.byContainer,
    ResultDeterminationType.byOneContainer,
    ResultDeterminationType.noResult,
    ResultDeterminationType.ByContainerAndProductBox,
];

export const getSortedActivitiesForManualInputFlow = (
    flowType: ManualInputFlowType,
    activities: Activity[],
): Activity[] => {
    return getActivitiesForManualInputFlow(flowType, activities).sort((a, b) => a.number - b.number);
};

const getActivitiesForManualInputFlow = (
    flowType: ManualInputFlowType,
    activities: Activity[],
): Activity[] => {
    switch (flowType) {
        case 'generic':
            return activities.filter(
                (a) =>
                    a.activityType === ActivityType.Generic &&
                    isLocationTypeAndResultDeterminationTypeSupported(a),
            );
        case 'packing':
            return activities.filter(
                (a) =>
                    a.activityType === ActivityType.Packing &&
                    isLocationTypeAndResultDeterminationTypeSupported(a),
            );
        case 'harvest':
            return activities.filter(
                (a) =>
                    a.activityType === ActivityType.Harvest &&
                    isLocationTypeAndResultDeterminationTypeSupported(a),
            );
        case 'time-registration':
            return activities.filter(isSupportedBreakActivity);
        case 'crop-maintenance':
            return activities.filter(
                (a) =>
                    a.activityType === ActivityType.CropMaintenance &&
                    isLocationTypeAndResultDeterminationTypeSupported(a),
            );
    }
};

export function isLocationTypeAndResultDeterminationTypeSupported(activity: Activity) {
    if (activity.resultDeterminationType) {
        if (!activity.locationType)
            return supportedResultDeterminationTypes.includes(activity.resultDeterminationType);
        return (
            supportedLocationTypes.includes(activity.locationType!) &&
            supportedResultDeterminationTypes.includes(activity.resultDeterminationType)
        );
    }
    //  LocationType can be null, therefore as long as it's null or belongs to a supportedLocationTypes, it will return true
    if (!activity.locationType) return true;
    return supportedLocationTypes.includes(activity.locationType);
}

export function sortActivities(list: Activity[]) {
    return [...list].sort((a, b) => a.number - b.number);
}

export function getActivityByNumber(activityList: Activity[], currentActivityNumber: number) {
    return activityList.find((a) => a.number === currentActivityNumber);
}

export function getActivityTitle(activity: Activity, batchName?: string) {
    const activityTitle = activity.name
        ? activity.number + ' \u{B7} ' + activity.name
        : activity.number.toString();
    return batchName ? `${activityTitle} \u{B7} ${batchName}` : activityTitle;
}

export function activityNumbersToActivities({
    activityNumbers,
    activities,
}: {
    activityNumbers: number[];
    activities: Activity[];
}) {
    return activityNumbers
        .map((activityNumber) => getActivityByNumber(activities, activityNumber))
        .filter((activity): activity is Activity => !!activity);
}

export function isActivtyResultDeterminationTypeContainProductBoxes(newActivity: Activity) {
    return newActivity.resultDeterminationType === ResultDeterminationType.byAmountAndProductBox;
}

export function isRequiredActivityEntityEqual(newActivity: Activity, currentActivity: Activity | undefined) {
    if (currentActivity === undefined) return false;
    return (
        newActivity.resultDeterminationType === currentActivity.resultDeterminationType &&
        newActivity.locationType === currentActivity.locationType &&
        newActivity.activityType === currentActivity.activityType &&
        newActivity.nrOfDecimalsAfterDecimalPoint === currentActivity.nrOfDecimalsAfterDecimalPoint &&
        newActivity.nrOfDecimalsBeforeDecimalPoint === currentActivity.nrOfDecimalsBeforeDecimalPoint &&
        newActivity.presentationUnit === currentActivity.presentationUnit &&
        newActivity.orderedProductBoxNumbers.length === currentActivity.orderedProductBoxNumbers.length
    );
}
